import { LockIcon, TrashIcon, XIcon } from '@phosphor-icons/react'
import { toZonedTime } from 'date-fns-tz'
import { useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetTimeBlockById } from '@/api/reservations/get-time-block-by-id'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { DEFAULT_TZ, EMPTY_VALUE } from '@/constants'
import { popupFactory } from '@/factories'
import { UserRoleEnum } from '@/models'
import { DeleteTimeBlockConfirmationPopup } from '@/pages/reservations/time-blocks/delete-time-block-confirmation-popup'
import { TimeBlockNotesModal } from '@/pages/reservations/time-blocks/time-block-notes-modal/time-block-notes-modal'
import {
  getReasonLabelMap,
  REASON_COLOR_MAP,
  REASON_ICON_MAP,
} from '@/pages/reservations/time-blocks/time-blocks.constants'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { selectUserData, selectUserRole } from '@/reducers/userDataReducer'
import { formatDateOnly, formatMinutes } from '@/utils/date'

import { isFullDayTimeBlock } from '../helpers'

const formatDateWithMinutes = (
  timestamp: number | null | undefined,
  minutes: number | null | undefined,
  timezone: string,
) => {
  if (!timestamp) return EMPTY_VALUE
  const date = formatDateOnly(toZonedTime(timestamp, timezone))
  return minutes == null ? date : `${date} ${formatMinutes(minutes)}`
}

interface JQueryLike {
  0?: HTMLElement
}

interface Props {
  open?: boolean
  timeBlockId: number
  target?: JQueryLike | null
  onClose: () => void
}

export const CalendarTimeBlockPopup = ({ open, timeBlockId, target, onClose }: Props) => {
  const { t } = useTranslation(['calendar', 'common', 'reservations'])
  const { t: tReservations } = useTranslation(['reservations'])
  const userRole = useSelector(selectUserRole)

  const activeBuilding = useSelector(selectActiveBuilding)
  const userData = useSelector(selectUserData)
  // Shift Managers have no active building in the selector - they're tied to their own building
  const timezone = activeBuilding?.timezone ?? userData?.buildingId?.timezone ?? DEFAULT_TZ

  const { data: timeBlock, isLoading } = useGetTimeBlockById(timeBlockId)

  const popupRef = useRef<HTMLDivElement>(null)
  const arrowRef = useRef<HTMLSpanElement>(null)
  const [arrowPosition, setArrowPosition] = useState<'left' | 'right'>('left')

  const hasPermissions = [
    UserRoleEnum.enum.Administrator,
    UserRoleEnum.enum.PropertyManager,
    UserRoleEnum.enum.ShiftManager,
  ].includes(userRole)
  // null for blocks created before reasons existed - those show as General
  const blockReason = timeBlock ? (timeBlock.reason ?? 'General') : undefined
  const reason = timeBlock?.reasonOther || blockReason
  const amenityName = timeBlock?.amenityId?.name
  const locationName = timeBlock?.locationId?.name || ''
  const headerText = timeBlock
    ? `${reason} - ${amenityName}${locationName ? ` (${locationName})` : ''}`
    : ''
  const notesCount = timeBlock?.notes?.length ? ` ${timeBlock?.notes?.length}` : ''
  const reasonLabelMap = getReasonLabelMap(tReservations)

  const isMultiday = !!timeBlock?.endDate && timeBlock.endDate > timeBlock.date
  const dateInfo: string = !timeBlock
    ? ''
    : !isMultiday && isFullDayTimeBlock(timeBlock)
      ? t('calendar:timeBlockPopup.allDay')
      : `${formatDateWithMinutes(timeBlock.date, timeBlock.startTime, timezone)} - ${formatDateWithMinutes(
          timeBlock.endDate ?? timeBlock.date,
          timeBlock.endTime,
          timezone,
        )}`

  // after render, so the popup is already display:block and has its real size
  useLayoutEffect(() => {
    const targetEl = target?.[0]
    const popup = popupRef.current
    const container = popup?.closest('.ctp-calendar')
    if (!open || !targetEl || !popup || !container) return

    const containerRect = container.getBoundingClientRect()
    const targetRect = targetEl.getBoundingClientRect()

    const targetTop = targetRect.top - containerRect.top + container.scrollTop
    const targetLeft = targetRect.left - containerRect.left + container.scrollLeft
    const targetRight = targetLeft + targetRect.width
    const spaceOnRight = container.clientWidth - (targetRight - container.scrollLeft)

    const placeRight = spaceOnRight >= popup.offsetWidth
    const left = placeRight ? targetRight : targetLeft - popup.offsetWidth

    const minTop = container.scrollTop
    const maxTop = container.scrollTop + container.clientHeight - popup.offsetHeight
    const top = Math.max(
      minTop,
      Math.min(maxTop, targetTop + targetRect.height / 2 - popup.offsetHeight / 2),
    )

    popup.style.left = `${left}px`
    popup.style.top = `${top}px`
    if (arrowRef.current) {
      arrowRef.current.style.top = `${targetTop + targetRect.height / 2 - top - 8}px`
    }
    setArrowPosition(placeRight ? 'left' : 'right')

    targetEl.classList.add('ctp-event--active')
    return () => targetEl.classList.remove('ctp-event--active')
  }, [open, target])

  const infoItems: { label: string; value: React.ReactNode; hidden?: boolean }[] = [
    {
      label: t('calendar:timeBlockPopup.amenity'),
      value: timeBlock?.amenityId.name,
    },
    {
      label: t('calendar:timeBlockPopup.reason'),
      value: blockReason ? (
        <Badge
          color={REASON_COLOR_MAP[blockReason]}
          icon={REASON_ICON_MAP[blockReason]}
          size="small"
          variant="light"
        >
          {blockReason === 'Other'
            ? timeBlock?.reasonOther || reasonLabelMap.Other
            : reasonLabelMap[blockReason]}
        </Badge>
      ) : (
        EMPTY_VALUE
      ),
    },
    {
      hidden: !timeBlock?.description,
      label: t('calendar:timeBlockPopup.description'),
      value: timeBlock?.description,
    },
    {
      label: t('calendar:timeBlockPopup.createdBy'),
      value: timeBlock?.createdBy
        ? `${timeBlock.createdBy.firstName || ''} ${timeBlock.createdBy.lastName || ''}`
        : EMPTY_VALUE,
    },
  ]

  const onDeleteClick = () => {
    popupFactory.open(DeleteTimeBlockConfirmationPopup, {
      onConfirm: onClose,
      timeBlockId,
    })
  }

  const onNotesClick = () => {
    popupFactory.open(TimeBlockNotesModal, {
      timeBlockId,
      timezone,
    })
  }

  return (
    <div
      ref={popupRef}
      className={`ctp-event-popup calendar-time-block-reservation-popup ${open ? 'ctp-event-popup--active' : ''} ${
        arrowPosition === 'right' ? 'ctp-event-popup--pos-right' : ''
      }`}
    >
      <span ref={arrowRef} className="ctp-event-popup__arrow" />
      <header className="calendar-time-block-reservation-popup__header timeBlock">
        <div className="title">
          <div className="reservation-item__icon timeBlock">
            <LockIcon />
          </div>

          <Typography as="span" size="heading-tiny" color="black800">
            {headerText}
          </Typography>

          <div className="close-button" onClick={onClose}>
            <XIcon />
          </div>
        </div>
        <div className="actions">
          {hasPermissions && (
            <Button
              use="secondary"
              loading={isLoading}
              disabled={isLoading}
              onClick={onDeleteClick}
            >
              <TrashIcon />
              {t('common:actions.delete')}
            </Button>
          )}
          <Button use="secondary" loading={isLoading} disabled={isLoading} onClick={onNotesClick}>
            {`${t('calendar:timeBlockPopup.notes')}${notesCount}`}
          </Button>
        </div>
      </header>
      <div className="ctp-event-popup__inner">
        <Typography size="heading-tiny">{dateInfo}</Typography>

        <div className="time-block-info-container">
          {infoItems.map(({ label, value, hidden }) => {
            if (hidden) {
              return null
            }

            return (
              <div key={label} className="time-block-info">
                <Typography key={label} size="text-medium" color="black500" weight="medium">
                  {label}
                </Typography>

                {typeof value === 'string' || typeof value === 'number' ? (
                  <Typography color="black800" size="text-medium" weight="medium">
                    {value}
                  </Typography>
                ) : (
                  value
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
